// RGPD Types (STORY-046 to STORY-048)

// Statut de conformite
export type ComplianceStatus = 'compliant' | 'partial' | 'non_compliant' | 'pending'

// Type de demande RGPD
export type DataRequestType = 'export' | 'deletion' | 'rectification' | 'portability'

// Statut de la demande
export type DataRequestStatus = 'pending' | 'in_progress' | 'completed' | 'rejected' | 'cancelled'

// Format d'export
export type ExportFormat = 'json' | 'pdf' | 'csv'

// Categories de donnees traitees
export type DataCategory =
  | 'identity'
  | 'contact'
  | 'financial'
  | 'commercial'
  | 'technical'
  | 'behavioral'

// Base legale du traitement
export type LegalBasis =
  | 'consent'
  | 'contract'
  | 'legal_obligation'
  | 'vital_interest'
  | 'public_interest'
  | 'legitimate_interest'

// Regle RGPD
export interface RGPDRule {
  id: string
  category: string
  title: string
  description: string
  article: string // Article du RGPD (ex: "Art. 5", "Art. 6")
  status: ComplianceStatus
  actions: string[]
  lastChecked: string | null
  notes: string | null
}

// Categorie de regles RGPD
export interface RGPDCategory {
  id: string
  name: string
  description: string
  icon: string
  rules: RGPDRule[]
  complianceRate: number
}

// Traitement de donnees documente
export interface DataProcessing {
  id: string
  created_at: string
  updated_at: string
  name: string
  purpose: string
  legal_basis: LegalBasis
  data_categories: DataCategory[]
  recipients: string[]
  retention_period: string // Duree de conservation
  security_measures: string[]
  data_transfers: boolean // Transferts hors UE
  transfer_details: string | null
  dpo_approval: boolean
  status: ComplianceStatus
}

// Demande de donnees (export/suppression)
export interface DataRequest {
  id: string
  created_at: string
  updated_at: string
  client_id: string
  client_name: string
  client_email: string
  type: DataRequestType
  status: DataRequestStatus
  format?: ExportFormat
  reason?: string
  processed_at: string | null
  processed_by: string | null
  export_url?: string | null
  notes: string | null
  expires_at?: string | null
  // Relations
  client?: {
    id: string
    full_name: string
    email: string
  }
  processor?: {
    id: string
    full_name: string
  }
}

// Audit de conformite
export interface ComplianceAudit {
  id: string
  created_at: string
  type: 'automatic' | 'manual'
  status: ComplianceStatus
  issues_found: number
  issues_resolved: number
  issues_pending: number
  details: ComplianceIssue[]
  report_url: string | null
  auditor_id: string | null
  auditor?: {
    id: string
    full_name: string
  }
}

// Probleme de conformite
export interface ComplianceIssue {
  id: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  category: string
  title: string
  description: string
  recommendation: string
  status: 'open' | 'in_progress' | 'resolved' | 'ignored'
  resolved_at: string | null
  resolved_by: string | null
}

// Statistiques RGPD
export interface RGPDStats {
  totalProcessings: number
  compliantProcessings: number
  pendingRequests: number
  completedRequests: number
  exportRequests: number
  deletionRequests: number
  lastAuditDate: string | null
  overallComplianceRate: number
  dataRetentionAlerts: number
}

// Alerte de donnees non conformes
export interface DataAlert {
  id: string
  created_at: string
  type: 'retention_expired' | 'consent_expired' | 'missing_basis' | 'security_issue' | 'incomplete_processing'
  severity: 'critical' | 'high' | 'medium' | 'low'
  title: string
  description: string
  affected_records: number
  affected_entity: string // ex: "clients", "contacts"
  entity_ids: string[]
  action_required: string
  resolved: boolean
  resolved_at: string | null
}

// Donnees client pour export
export interface ClientDataExport {
  personal_info: {
    id: string
    full_name: string
    email: string
    phone: string | null
    company: string | null
    type_client: string
    created_at: string
  }
  billing_info: {
    address: string | null
    siret: string | null
    tva: string | null
  }
  projects: Array<{
    id: string
    name: string
    status: string
    created_at: string
    completed_at: string | null
  }>
  invoices: Array<{
    id: string
    number: string
    amount: number
    status: string
    date: string
  }>
  quotes: Array<{
    id: string
    number: string
    amount: number
    status: string
    date: string
  }>
  communications: Array<{
    type: string
    subject: string
    date: string
  }>
  consent_history: Array<{
    type: string
    given: boolean
    date: string
  }>
}

// Filtres pour les demandes
export interface DataRequestFilters {
  search?: string
  type?: DataRequestType
  status?: DataRequestStatus
  dateFrom?: string
  dateTo?: string
}

// Labels
export const dataRequestTypeLabels: Record<DataRequestType, string> = {
  export: 'Export de donnees',
  deletion: 'Suppression',
  rectification: 'Rectification',
  portability: 'Portabilite',
}

export const dataRequestStatusLabels: Record<DataRequestStatus, string> = {
  pending: 'En attente',
  in_progress: 'En cours',
  completed: 'Terminee',
  rejected: 'Rejetee',
  cancelled: 'Annulee',
}

export const complianceStatusLabels: Record<ComplianceStatus, string> = {
  compliant: 'Conforme',
  partial: 'Partiellement conforme',
  non_compliant: 'Non conforme',
  pending: 'A verifier',
}

export const legalBasisLabels: Record<LegalBasis, string> = {
  consent: 'Consentement',
  contract: 'Execution du contrat',
  legal_obligation: 'Obligation legale',
  vital_interest: 'Interet vital',
  public_interest: 'Interet public',
  legitimate_interest: 'Interet legitime',
}

export const dataCategoryLabels: Record<DataCategory, string> = {
  identity: 'Identite',
  contact: 'Coordonnees',
  financial: 'Financieres',
  commercial: 'Commerciales',
  technical: 'Techniques',
  behavioral: 'Comportementales',
}

// Couleurs pour les badges
export const dataRequestStatusColors: Record<DataRequestStatus, { variant: 'mint' | 'gold' | 'brown' | 'red' | 'gray' }> = {
  pending: { variant: 'gold' },
  in_progress: { variant: 'brown' },
  completed: { variant: 'mint' },
  rejected: { variant: 'red' },
  cancelled: { variant: 'gray' },
}

export const complianceStatusColors: Record<ComplianceStatus, { variant: 'mint' | 'gold' | 'red' | 'gray' }> = {
  compliant: { variant: 'mint' },
  partial: { variant: 'gold' },
  non_compliant: { variant: 'red' },
  pending: { variant: 'gray' },
}

export const severityColors: Record<'critical' | 'high' | 'medium' | 'low', { variant: 'red' | 'gold' | 'brown' | 'gray' }> = {
  critical: { variant: 'red' },
  high: { variant: 'gold' },
  medium: { variant: 'brown' },
  low: { variant: 'gray' },
}
